// client/src/utils/ensureUserSession.js
//
// Point d'entrée UNIQUE pour obtenir une session Firebase (scan QR, shortlink, etc.).
//
// Ordre strict, sans minuterie qui court-circuite la restauration :
//   1. attendre que Firebase ait fini de restaurer la session locale (IndexedDB) → authStateReady()
//   2. sinon, restaurer le compte associé à l'appareil (cookie serveur, survit à la purge iOS)
//   3. sinon seulement, et si autorisé, créer un compte anonyme
//
// Un appel concurrent réutilise la même promesse : impossible de créer deux anonymes en parallèle,
// et impossible d'écraser un compte officiel par un anonyme (on ne crée JAMAIS d'anonyme
// tant qu'un utilisateur est présent ou en cours de restauration).
import { signInAnonymously, signInWithCustomToken } from 'firebase/auth';
import { auth, persistenceReady } from '../firebase';
import { registerDeviceLink, resolveDeviceLink } from './deviceLink';

const UID_STORAGE_KEY = 'ecobill_last_uid';
const LOGOUT_GUARD_KEY = 'ecobill_logout_guard';

let inFlight = null;
// Une seule tentative de restauration appareil par chargement de page (sauf nouvelle association)
let deviceRestoreTried = false;

function readLogoutGuard() {
  try { return localStorage.getItem(LOGOUT_GUARD_KEY) === '1'; } catch { return false; }
}

function clearLogoutGuard() {
  try { localStorage.removeItem(LOGOUT_GUARD_KEY); } catch {}
}

function onSessionReady(user) {
  try { localStorage.setItem(UID_STORAGE_KEY, user.uid); } catch {}
  registerDeviceLink().catch(() => {});
  return user;
}

/** Résout quand Firebase a terminé la restauration de la session locale. */
export async function waitForAuthReady() {
  try { await persistenceReady; } catch {}
  await auth.authStateReady();
  return auth.currentUser;
}

async function restoreFromDevice() {
  if (deviceRestoreTried) return null;
  deviceRestoreTried = true;
  const payload = await resolveDeviceLink();
  if (!payload?.customToken) return null;
  // Un autre flux a pu connecter quelqu'un pendant l'appel réseau : on ne l'écrase pas
  if (auth.currentUser) return auth.currentUser;
  try {
    const { user } = await signInWithCustomToken(auth, payload.customToken);
    return user;
  } catch (error) {
    console.warn('[ensureUserSession] device restore sign-in failed', error?.code || error);
    return null;
  }
}

async function run({ allowAnon, overrideLogoutGuard }) {
  const existing = await waitForAuthReady();
  if (existing) return onSessionReady(existing);

  const logoutGuard = readLogoutGuard();

  // Après une déconnexion volontaire, on ne ré-ouvre pas l'ancien compte automatiquement
  if (!logoutGuard) {
    const restored = await restoreFromDevice();
    if (restored) return onSessionReady(restored);
  }

  if (auth.currentUser) return onSessionReady(auth.currentUser);

  if (!allowAnon || (logoutGuard && !overrideLogoutGuard)) {
    const err = new Error(logoutGuard ? 'logout-guard-active' : 'no-session');
    err.code = logoutGuard ? 'logout_guard' : 'no_session';
    throw err;
  }

  const { user } = await signInAnonymously(auth);
  clearLogoutGuard();
  return onSessionReady(user);
}

/**
 * @param {object}  [options]
 * @param {boolean} [options.allowNewAnonymous] crée un compte anonyme si aucune session n'existe
 * @param {boolean} [options.allowAnonymous]    alias historique de allowNewAnonymous
 * @param {boolean} [options.overrideLogoutGuard] autorise l'anonyme même après une déconnexion volontaire
 * Les anciennes options (graceMs, fallbackMs, ignoreLastSeenUid) sont ignorées.
 */
export function ensureUserSession(options = {}) {
  const allowAnon = Boolean(options.allowNewAnonymous ?? options.allowAnonymous);
  const overrideLogoutGuard = Boolean(options.overrideLogoutGuard);

  if (inFlight) {
    // Un appel restrictif en cours (ex. bootstrap) ne doit pas faire échouer un appel qui autorise l'anonyme
    return inFlight.catch(() => null).then((user) => {
      if (user) return user;
      return ensureUserSession(options);
    });
  }

  inFlight = run({ allowAnon, overrideLogoutGuard }).finally(() => { inFlight = null; });
  return inFlight;
}
